import * as THREE from "three";
import { DXF_VARS } from "../../Global/variables";

/**
 * Projects a 3D dormer into a 2D plane using the defined vectors for U and V.
 * 
 * @param {Array} dormerPoints - An array of THREE.Vector3 points representing the 3D coordinates of the dormer.
 * @returns {Array} The transformed dormer points after projection and repositioning.
 */
export function projectDormerTo2D(dormerPoints) {
    // Project the 3D points onto the 2D plane defined by the U and V vectors
    let projectedPoints = dormerPoints.map(point => {
        point = new THREE.Vector3(point.x, point.y, point.z); // Ensure the point is in 3D vector format
        const xCoordinate = point.dot(DXF_VARS.vectorU); // Calculate the x-coordinate on the 2D plane
        const yCoordinate = point.dot(DXF_VARS.vectorV); // Calculate the y-coordinate on the 2D plane
        return new THREE.Vector2(xCoordinate, yCoordinate); // Return as a 2D vector
    });

    // Close the polygon by repeating the first point at the end
    projectedPoints.push(projectedPoints[0]);

    // Position the dormer correctly with respect to the origin
    return dormerPositioning(projectedPoints);
}

/**
 * Translates and rotates the dormer points, and optionally flips them vertically.
 * 
 * @param {Array} dormerPoints - The 2D points of the dormer.
 * @returns {Array} The transformed dormer points after translation, rotation, and flipping.
 */
function dormerPositioning(dormerPoints) {
    let origin = DXF_VARS.origin; // Get the origin point
    const translationVector = new THREE.Vector2(-origin.x, -origin.y); // Calculate translation vector to move dormer relative to origin

    // Translate the dormer points by subtracting the origin from each point
    const translatedDormerPoints = dormerPoints.map(point => {
        const translatedPoint = point.clone().add(translationVector); // Apply the translation to each point
        return { x: translatedPoint.x, y: translatedPoint.y }; // Return the translated point
    });

    let lineAngle = DXF_VARS.rotationAngle; // Get the rotation angle
    // Rotate the translated dormer points by the specified rotation angle
    const rotatedDormerPoints = translatedDormerPoints.map(point => {
        const rotatedX = Math.cos(-lineAngle) * point.x - Math.sin(-lineAngle) * point.y; // Rotate the x-coordinate
        const rotatedY = Math.sin(-lineAngle) * point.x + Math.cos(-lineAngle) * point.y; // Rotate the y-coordinate
        return new THREE.Vector3(rotatedX, rotatedY, point.z); // Return the rotated point as a 3D vector (with unchanged z-coordinate)
    });

    // If vertical flipping is required, invert the y-coordinate of each point
    if (DXF_VARS.isVerticalFlipped) {
        rotatedDormerPoints.forEach(point => {
            point.y = -point.y; // Flip the y-coordinate vertically
        });
    }

    return rotatedDormerPoints; // Return the transformed dormer points
}
